// src/store/slices/gameSlice.js
import { createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { runAdminAuthedRequest } from '../../utils/adminAuth';

const HALFTIME_DURATION_SEC = 3 * 60;

const initialState = {
  game: null,
  gameId: null,

  phase: 1,

  // keyed by player.email
  attendance: {},
  goals: {},
  assists: {},
  cards: {}, // 'none' | 'yellow' | 'red' (current UI state)
  redCardUntil: {}, // epoch ms per player
  cardCounts: {}, // { [email]: { yellow: number, red: number } }
  matchEvents: [],

  // timer is derived from startedAt + durationSec
  timer: 0,
  startedAt: null,     // epoch ms
  durationSec: 0,      // seconds
  halftimeStartedAt: null,
  halftimeCompleted: false,
  pausedTimerSec: null,

  // team-owned score
  score: [0, 0],

  loading: false,
  error: null,
};

/* =========================
   LocalStorage persistence
   ========================= */

function storageKey(gameId) {
  return `zealfc:gameSession:${gameId}`;
}

function safeLoadSession(gameId) {
  try {
    const raw = localStorage.getItem(storageKey(gameId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.gameId !== gameId) return null;
    return parsed;
  } catch {
    return null;
  }
}

function safeSaveSession(gameId, session) {
  try {
    localStorage.setItem(storageKey(gameId), JSON.stringify(session));
  } catch {
    // ignore quota / private mode failures
  }
}

function safeClearSession(gameId) {
  try {
    localStorage.removeItem(storageKey(gameId));
  } catch {
    // ignore
  }
}

/**
 * Build a serializable session snapshot from state.
 * IMPORTANT: keep session data scoped to runtime state only.
 */
function buildSessionSnapshot(state) {
  const g = state.game;
  const gameId = state.gameId || g?._id;

  if (!gameId) return null;

  return {
    version: 1,
    gameId,
    phase: state.phase,

    // match clock
    startedAt: state.startedAt,
    durationSec: state.durationSec,
    halftimeStartedAt: state.halftimeStartedAt,
    halftimeCompleted: state.halftimeCompleted,
    pausedTimerSec: state.pausedTimerSec,

    // stats
    attendance: state.attendance,
    goals: state.goals,
    assists: state.assists,
    cards: state.cards,
    redCardUntil: state.redCardUntil,
    cardCounts: state.cardCounts,
    matchEvents: state.matchEvents,
    score: state.score,
  };
}

/**
 * (Optional helper) Find which team a player belongs to by email.
 * Used only as fallback when teamIndex not provided.
 */
function findTeamIndexByEmail(game, email) {
  if (!game?.teams || !email) return null;
  for (let t = 0; t < game.teams.length; t++) {
    for (let i = 0; i < game.teams[t].length; i++) {
      const p = game.teams[t][i];
      if (p?.email === email) return t;
    }
  }
  return null;
}

/**
 * Compute remaining timer seconds based on startedAt/durationSec.
 */
function computeRemainingSec(startedAt, durationSec, nowMs) {
  if (!startedAt || !durationSec) return durationSec || 0;
  const elapsed = Math.floor((nowMs - startedAt) / 1000);
  return Math.max(0, (durationSec || 0) - elapsed);
}

function computeTimerSnapshot({
  startedAt,
  durationSec,
  halftimeStartedAt,
  pausedTimerSec,
  nowMs,
}) {
  if (!startedAt || !durationSec) {
    return {
      timer: durationSec || 0,
      startedAt,
      halftimeStartedAt,
      pausedTimerSec,
    };
  }

  if (halftimeStartedAt) {
    const halftimeElapsedSec = Math.floor((nowMs - halftimeStartedAt) / 1000);
    const halftimeRemainingSec = Math.max(0, HALFTIME_DURATION_SEC - halftimeElapsedSec);

    return {
      timer: halftimeRemainingSec,
      startedAt,
      halftimeStartedAt,
      pausedTimerSec,
    };
  }

  return {
    timer: computeRemainingSec(startedAt, durationSec, nowMs),
    startedAt,
    halftimeStartedAt,
    pausedTimerSec,
  };
}

function clampDropIndex(team, requestedIndex) {
  const safeTeamLength = Array.isArray(team) ? team.length : 0;
  if (safeTeamLength <= 0) {
    return 0;
  }

  const normalizedIndex = Number(requestedIndex);
  if (!Number.isInteger(normalizedIndex) || normalizedIndex < 0) {
    return 0;
  }

  return Math.min(normalizedIndex, safeTeamLength - 1);
}

function resetRuntimeStateFields(state) {
  state.phase = 1;
  state.attendance = {};
  state.goals = {};
  state.assists = {};
  state.cards = {};
  state.redCardUntil = {};
  state.cardCounts = {};
  state.matchEvents = [];
  state.score = [0, 0];

  state.timer = 0;
  state.startedAt = null;
  state.durationSec = 0;
  state.halftimeStartedAt = null;
  state.halftimeCompleted = false;
  state.pausedTimerSec = null;
}

const gameSlice = createSlice({
  name: 'game',
  initialState,
  reducers: {
    /**
     * Set game document.
     * Preserve same-game runtime state, but clear stale in-memory session data
     * when navigating to a different game. Session restore still happens in
     * hydrateSessionFromStorage/fetchGame.
     */
    setGame(state, action) {
      const nextGameId = action.payload?._id || null;
      const isSwitchingGames = Boolean(nextGameId && state.gameId && nextGameId !== state.gameId);

      if (isSwitchingGames) {
        resetRuntimeStateFields(state);
      }

      state.game = action.payload;
      state.gameId = nextGameId || state.gameId;

      // Initialize attendance defaults if not already set
      const nextAttendance = {};
      action.payload?.teams?.flat()?.forEach((p) => {
        if (p?.email) nextAttendance[p.email] = p.attendance || 'registered';
      });

      // only fill missing keys, don't blow away existing attendance
      state.attendance = { ...nextAttendance, ...state.attendance };
      state.matchEvents = Array.isArray(action.payload?.matchEvents)
        ? action.payload.matchEvents
        : [];
    },

    setPhase(state, action) {
      state.phase = action.payload;

      // persist if we have a gameId
      const snapshot = buildSessionSnapshot(state);
      if (snapshot?.gameId) safeSaveSession(snapshot.gameId, snapshot);
    },

    /**
     * Called when the actual match starts (Phase 3 begins).
     * Sets startedAt once and persists.
     */
    startGameSession(state) {
      if (!state.game) return;

      state.phase = 3;

      if (!state.durationSec) {
        state.durationSec = (state.game.duration || 0) * 60;
      }

      if (!state.startedAt) {
        state.startedAt = Date.now(); // moment the match started
      }

      const nextTimer = computeTimerSnapshot({
        startedAt: state.startedAt,
        durationSec: state.durationSec,
        halftimeStartedAt: state.halftimeStartedAt,
        pausedTimerSec: state.pausedTimerSec,
        nowMs: Date.now(),
      });

      state.timer = nextTimer.timer;
      state.startedAt = nextTimer.startedAt;
      state.halftimeStartedAt = nextTimer.halftimeStartedAt;
      state.pausedTimerSec = nextTimer.pausedTimerSec;

      const snapshot = buildSessionSnapshot(state);
      if (snapshot?.gameId) safeSaveSession(snapshot.gameId, snapshot);
    },

    /**
     * Hydrate entire session state from localStorage.
     * Must be called after setGame so runtime values merge with server game doc.
     */
    hydrateSession(state, action) {
      const session = action.payload;
      if (!session?.gameId) return;

      state.gameId = session.gameId;
      state.phase = session.phase ?? state.phase;

      state.startedAt = session.startedAt ?? state.startedAt;
      state.durationSec = session.durationSec ?? state.durationSec;
      state.halftimeStartedAt = session.halftimeStartedAt ?? state.halftimeStartedAt;
      state.halftimeCompleted = session.halftimeCompleted ?? state.halftimeCompleted;
      state.pausedTimerSec = session.pausedTimerSec ?? state.pausedTimerSec;

      state.attendance = session.attendance || state.attendance;
      state.goals = session.goals || state.goals;
      state.assists = session.assists || state.assists;
      state.cards = session.cards || state.cards;
      state.redCardUntil = session.redCardUntil || state.redCardUntil;
      state.cardCounts = session.cardCounts || state.cardCounts;
      state.matchEvents = session.matchEvents || state.matchEvents;
      state.score = session.score || state.score;

      const nextTimer = computeTimerSnapshot({
        startedAt: state.startedAt,
        durationSec: state.durationSec,
        halftimeStartedAt: state.halftimeStartedAt,
        pausedTimerSec: state.pausedTimerSec,
        nowMs: Date.now(),
      });

      state.timer = nextTimer.timer;
      state.startedAt = nextTimer.startedAt;
      state.halftimeStartedAt = nextTimer.halftimeStartedAt;
      state.pausedTimerSec = nextTimer.pausedTimerSec;
    },

    /**
     * Called every second (or on demand) to keep timer accurate across refresh.
     * Payload is nowMs = Date.now()
     */
    syncTimer(state, action) {
      const nowMs = typeof action.payload === 'number' ? action.payload : Date.now();
      const nextTimer = computeTimerSnapshot({
        startedAt: state.startedAt,
        durationSec: state.durationSec,
        halftimeStartedAt: state.halftimeStartedAt,
        pausedTimerSec: state.pausedTimerSec,
        nowMs,
      });

      state.timer = nextTimer.timer;
      state.startedAt = nextTimer.startedAt;
      state.halftimeStartedAt = nextTimer.halftimeStartedAt;
      state.pausedTimerSec = nextTimer.pausedTimerSec;

      // Persist occasionally is OK; here we persist every tick (safe but chatty).
      // If you want less writes, persist only on state changes (goal/move/etc).
      const snapshot = buildSessionSnapshot(state);
      if (snapshot?.gameId) safeSaveSession(snapshot.gameId, snapshot);
    },

    startHalftime(state) {
      if (!state.startedAt || state.halftimeStartedAt) return;

      state.halftimeStartedAt = Date.now();
      state.halftimeCompleted = false;
      state.pausedTimerSec = state.timer;
      state.timer = HALFTIME_DURATION_SEC;

      const snapshot = buildSessionSnapshot(state);
      if (snapshot?.gameId) safeSaveSession(snapshot.gameId, snapshot);
    },

    endHalftime(state) {
      if (!state.startedAt || !state.halftimeStartedAt) return;

      const nowMs = Date.now();
      const resumedTimer =
        Number.isFinite(state.pausedTimerSec) && state.pausedTimerSec !== null
          ? state.pausedTimerSec
          : computeRemainingSec(state.startedAt, state.durationSec, nowMs);
      const elapsedMatchSec = Math.max(0, state.durationSec - resumedTimer);

      state.startedAt = nowMs - elapsedMatchSec * 1000;
      state.timer = resumedTimer;
      state.halftimeStartedAt = null;
      state.halftimeCompleted = true;
      state.pausedTimerSec = null;

      const snapshot = buildSessionSnapshot(state);
      if (snapshot?.gameId) safeSaveSession(snapshot.gameId, snapshot);
    },

    setAttendance(state, action) {
      state.attendance = action.payload || {};

      const snapshot = buildSessionSnapshot(state);
      if (snapshot?.gameId) safeSaveSession(snapshot.gameId, snapshot);
    },

    updateAttendance(state, action) {
      const { key, status } = action.payload;
      if (!key) return;
      state.attendance[key] = status;

      const snapshot = buildSessionSnapshot(state);
      if (snapshot?.gameId) safeSaveSession(snapshot.gameId, snapshot);
    },

    setGoals(state, action) {
      state.goals = action.payload || {};

      const snapshot = buildSessionSnapshot(state);
      if (snapshot?.gameId) safeSaveSession(snapshot.gameId, snapshot);
    },

    updateGoal(state, action) {
      const { key, teamIndex } = action.payload;
      if (!key) return;

      state.goals[key] = (state.goals[key] || 0) + 1;

      let idx =
        typeof teamIndex === 'number' ? teamIndex : findTeamIndexByEmail(state.game, key);

      if (idx === 0 || idx === 1) {
        state.score[idx] = (state.score[idx] || 0) + 1;
      }

      const snapshot = buildSessionSnapshot(state);
      if (snapshot?.gameId) safeSaveSession(snapshot.gameId, snapshot);
    },

    setAssists(state, action) {
      state.assists = action.payload || {};

      const snapshot = buildSessionSnapshot(state);
      if (snapshot?.gameId) safeSaveSession(snapshot.gameId, snapshot);
    },

    updateAssist(state, action) {
      const { key } = action.payload;
      if (!key) return;

      state.assists[key] = (state.assists[key] || 0) + 1;

      const snapshot = buildSessionSnapshot(state);
      if (snapshot?.gameId) safeSaveSession(snapshot.gameId, snapshot);
    },

    updateAssistDelta(state, action) {
      const { key, delta } = action.payload;
      if (!key) return;

      const change = typeof delta === 'number' ? delta : 1;
      const current = state.assists[key] || 0;
      const next = Math.max(0, current + change);
      state.assists[key] = next;

      const snapshot = buildSessionSnapshot(state);
      if (snapshot?.gameId) safeSaveSession(snapshot.gameId, snapshot);
    },

    updateGoalDelta(state, action) {
      const { key, teamIndex, delta } = action.payload;
      if (!key) return;

      const change = typeof delta === 'number' ? delta : 1;
      const current = state.goals[key] || 0;
      const next = Math.max(0, current + change);
      const applied = next - current;

      state.goals[key] = next;

      let idx =
        typeof teamIndex === 'number' ? teamIndex : findTeamIndexByEmail(state.game, key);

      if (applied !== 0 && (idx === 0 || idx === 1)) {
        state.score[idx] = Math.max(0, (state.score[idx] || 0) + applied);
      }

      const snapshot = buildSessionSnapshot(state);
      if (snapshot?.gameId) safeSaveSession(snapshot.gameId, snapshot);
    },

    updateCard(state, action) {
      const { key } = action.payload;
      if (!key) return;

      const current = state.cards[key] || 'none';

      if (!state.cardCounts[key]) {
        state.cardCounts[key] = { yellow: 0, red: 0 };
      }

      if (current === 'none') {
        state.cards[key] = 'yellow';
        state.cardCounts[key].yellow += 1;
        delete state.redCardUntil[key];
      } else if (current === 'yellow') {
        state.cards[key] = 'red';
        state.cardCounts[key].red += 1;
        state.redCardUntil[key] = Date.now() + 2 * 60 * 1000;
      } else {
        state.cards[key] = 'none';
        delete state.redCardUntil[key];
      }

      const snapshot = buildSessionSnapshot(state);
      if (snapshot?.gameId) safeSaveSession(snapshot.gameId, snapshot);
    },

    updateCardCorrect(state, action) {
      const { key } = action.payload;
      if (!key) return;

      const current = state.cards[key] || 'none';

      if (!state.cardCounts[key]) {
        state.cardCounts[key] = { yellow: 0, red: 0 };
      }

      if (current === 'red') {
        state.cards[key] = 'yellow';
        if (state.cardCounts[key].red > 0) {
          state.cardCounts[key].red -= 1;
        }
        delete state.redCardUntil[key];
      } else if (current === 'yellow') {
        state.cards[key] = 'none';
        if (state.cardCounts[key].yellow > 0) {
          state.cardCounts[key].yellow -= 1;
        }
      }

      const snapshot = buildSessionSnapshot(state);
      if (snapshot?.gameId) safeSaveSession(snapshot.gameId, snapshot);
    },

    tickRedCards(state, action) {
      const nowMs = typeof action.payload === 'number' ? action.payload : Date.now();
      let changed = false;

      Object.keys(state.redCardUntil || {}).forEach((email) => {
        const expiresAt = state.redCardUntil[email];
        if (!expiresAt || nowMs >= expiresAt) {
          state.cards[email] = 'none';
          delete state.redCardUntil[email];
          changed = true;
        }
      });

      if (changed) {
        const snapshot = buildSessionSnapshot(state);
        if (snapshot?.gameId) safeSaveSession(snapshot.gameId, snapshot);
      }
    },

    setTimer(state, action) {
      state.timer = action.payload;
    },

    setLoading(state, action) {
      state.loading = action.payload;
    },

    setError(state, action) {
      state.error = action.payload;
    },

    setScore(state, action) {
      state.score = action.payload;

      const snapshot = buildSessionSnapshot(state);
      if (snapshot?.gameId) safeSaveSession(snapshot.gameId, snapshot);
    },

    setMatchEvents(state, action) {
      state.matchEvents = Array.isArray(action.payload) ? action.payload : [];

      const snapshot = buildSessionSnapshot(state);
      if (snapshot?.gameId) safeSaveSession(snapshot.gameId, snapshot);
    },

    movePlayer(state, action) {
      const { fromTeam, fromIndex, toTeam, toIndex } = action.payload;
      if (!state.game || !Array.isArray(state.game.teams)) return;

      const teamsCopy = state.game.teams.map((team) => team.slice());
      const safeToIndex = clampDropIndex(teamsCopy[toTeam], toIndex);

      const movingPlayer = teamsCopy[fromTeam]?.[fromIndex] || null;
      const targetPlayer = teamsCopy[toTeam]?.[safeToIndex] || null;

      teamsCopy[toTeam][safeToIndex] = movingPlayer;
      teamsCopy[fromTeam][fromIndex] = targetPlayer || null;

      state.game = { ...state.game, teams: teamsCopy };

      const snapshot = buildSessionSnapshot(state);
      if (snapshot?.gameId) safeSaveSession(snapshot.gameId, snapshot);
    },

    updatePlayerPaymentStatus(state, action) {
      const { email, paymentStatus, paymentMethod, cashDueAt, cashPaidAt, cashMarkedBy } = action.payload || {};
      if (!state.game || !Array.isArray(state.game.teams) || !email) return;

      const targetEmail = String(email).toLowerCase();
      const nextTeams = state.game.teams.map((team) =>
        team.map((player) => {
          if (!player?.email || String(player.email).toLowerCase() !== targetEmail) {
            return player;
          }

          return {
            ...player,
            paymentStatus: paymentStatus || player.paymentStatus,
            paymentMethod: paymentMethod || player.paymentMethod,
            cashDueAt: cashDueAt ?? player.cashDueAt ?? null,
            cashPaidAt: cashPaidAt ?? null,
            cashMarkedBy: cashMarkedBy ?? player.cashMarkedBy ?? null,
          };
        })
      );

      state.game = { ...state.game, teams: nextTeams };
    },

    /**
     * Clear everything for this game session (e.g. when finishing the game).
     */
    clearGameSession(state) {
      const gid = state.gameId || state.game?._id;
      if (gid) safeClearSession(gid);

      resetRuntimeStateFields(state);
    },
  },
});

export const {
  setGame,
  setPhase,
  startGameSession,
  hydrateSession,
  syncTimer,
  startHalftime,
  endHalftime,
  setAttendance,
  updateAttendance,
  setGoals,
  updateGoal,
  setAssists,
  updateAssist,
  updateGoalDelta,
  updateAssistDelta,
  updateCard,
  updateCardCorrect,
  tickRedCards,
  setTimer,
  setLoading,
  setError,
  setScore,
  setMatchEvents,
  movePlayer,
  updatePlayerPaymentStatus,
  clearGameSession,
} = gameSlice.actions;

/**
 * Thunk: fetch a game by ID.
 * - If a session exists in localStorage, restore runtime state (phase/timer/stats).
 * - Otherwise initialize durationSec/timer from game duration.
 */
export const fetchGame = (gameId, { onAuthFailure } = {}) => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));
  try {
    const response = await runAdminAuthedRequest(
      (idToken) =>
        axios.get(`${process.env.REACT_APP_API_URL}/games/managed/${gameId}`, {
          headers: {
            Authorization: `Bearer ${idToken}`,
            'Content-Type': 'application/json',
          },
        }),
      { onAuthFailure }
    );
    if (!response) {
      dispatch(setError('Authentication required.'));
      return;
    }

    const game = response.data;

    const session = safeLoadSession(gameId);

    dispatch(setGame(game));

    // If there is a saved session, hydrate stats/phase/timer
    if (session) {
      dispatch(hydrateSession(session));
    } else {
      // Fresh view (no running session)
      const durationSec = (game.duration || 0) * 60;
      dispatch(setTimer(durationSec));
    }
  } catch (error) {
    console.error('Error fetching game', error);
    dispatch(setError(error?.response?.data?.error || 'Error fetching game'));
  } finally {
    dispatch(setLoading(false));
  }
};

export default gameSlice.reducer;
